import { ChevronRight } from "lucide-react";
import { memo, useCallback, type ReactNode } from "react";
import { cn } from "@/modern/utils";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { navigateToViewByType } from "@/modern/utils";
import { PageGuideTrigger } from "@/react/guides";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import {
  MARKETING_STACK_STEPS,
  type MarketingStackStep,
  type MarketingStackViewType,
} from "./constants";
import { marketingStackStripClassName } from "./layout";
import { useActiveBoardContext } from "./useActiveBoardContext";
import {
  buildContextNavigationParams,
  resolveStepSubtitle,
  stepAccessibleLabel,
  type MarketingStackStepOverride,
} from "./workflowStripHelpers";

export type { MarketingStackStepOverride };

export interface MarketingStackWorkflowStripProps {
  activeView: MarketingStackViewType;
  contextLabel?: string | null;
  trailing?: ReactNode;
  /** Renders inside the nav immediately after the matching step (e.g. Tags after Manifest). */
  navAfterStep?: Partial<Record<MarketingStackViewType, ReactNode>>;
  hint?: ReactNode;
  stepOverrides?: Partial<Record<MarketingStackViewType, MarketingStackStepOverride>>;
  className?: string;
}

const linkStepClassName = cn(
  "group inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-left",
  "border-border/70 bg-background/80 transition-colors",
  "hover:border-primary/40 hover:bg-primary/5",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
);

const activeStepClassName = cn(
  "inline-flex max-w-full items-center gap-1 rounded-md border py-1 pl-2",
  "border-primary/60 bg-primary/15 text-foreground pr-1.5",
);

function StepChevron() {
  return (
    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" aria-hidden />
  );
}

function ContextNameLabel({ name, active }: { name: string; active?: boolean }) {
  return (
    <>
      <span className="text-muted-foreground/35" aria-hidden>
        ·
      </span>
      <span
        className={cn(
          "max-w-[120px] truncate text-[11px] font-normal leading-tight",
          active ? "text-muted-foreground" : "text-muted-foreground/65 group-hover:text-muted-foreground",
        )}
      >
        {name}
      </span>
    </>
  );
}

function StepSubtitle({
  subtitle,
  active,
  isContextName,
}: {
  subtitle: string;
  active?: boolean;
  isContextName?: boolean;
}) {
  if (isContextName) {
    return <ContextNameLabel name={subtitle} active={active} />;
  }

  return (
    <span className="max-w-[120px] truncate text-xs font-normal text-muted-foreground">{subtitle}</span>
  );
}

function MarketingStackStepLink({
  step,
  subtitle,
  isContextName,
  onClick,
  stepProps,
}: {
  step: MarketingStackStep;
  subtitle?: string | null;
  isContextName?: boolean;
  onClick: () => void;
  stepProps?: Record<string, unknown>;
}) {
  const Icon = step.icon;
  const accessibleLabel = stepAccessibleLabel(step, subtitle ?? null);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={accessibleLabel}
      title={accessibleLabel}
      className={linkStepClassName}
      {...stepProps}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-primary" aria-hidden />
      <span className="text-sm font-normal text-muted-foreground group-hover:text-foreground">
        {step.label}
      </span>
      {subtitle ? (
        <StepSubtitle subtitle={subtitle} isContextName={isContextName} />
      ) : null}
    </button>
  );
}

function MarketingStackStepActive({
  step,
  subtitle,
  isContextName,
  onShowGuide,
  stepProps,
}: {
  step: MarketingStackStep;
  subtitle?: string | null;
  isContextName?: boolean;
  onShowGuide?: () => void;
  stepProps?: Record<string, unknown>;
}) {
  const Icon = step.icon;
  const accessibleLabel = stepAccessibleLabel(step, subtitle ?? null);

  return (
    <div
      aria-current="page"
      aria-label={accessibleLabel}
      title={accessibleLabel}
      className={activeStepClassName}
      {...stepProps}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
      <span className="text-sm font-semibold">{step.label}</span>
      {subtitle ? (
        <StepSubtitle subtitle={subtitle} active isContextName={isContextName} />
      ) : null}
      {onShowGuide ? (
        <>
          <span className="mx-0.5 h-3 w-px shrink-0 bg-primary/25" aria-hidden />
          <PageGuideTrigger onClick={onShowGuide} variant="inline" />
        </>
      ) : null}
    </div>
  );
}

export const MarketingStackWorkflowStrip = memo(function MarketingStackWorkflowStrip({
  activeView,
  contextLabel,
  trailing,
  navAfterStep,
  hint,
  stepOverrides,
  className,
}: MarketingStackWorkflowStripProps) {
  const { viewManager } = useSwivuBryntumContext();
  const { activeContext, contextLabel: activeBoardContextLabel } = useActiveBoardContext();

  const navigateToStep = useCallback(
    (step: MarketingStackStep) => {
      if (!viewManager) return;
      navigateToViewByType(
        viewManager,
        step.viewType,
        step.viewName,
        buildContextNavigationParams(step, activeContext?.id),
      );
    },
    [viewManager, activeContext?.id],
  );

  return (
    <div className={cn(marketingStackStripClassName, className)}>
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-card/50 px-2 py-1.5 sm:gap-2 sm:px-2.5">
        <nav
          aria-label="Marketing stack navigation"
          className="flex min-w-0 flex-1 flex-wrap items-center gap-1 sm:gap-1.5"
        >
          {MARKETING_STACK_STEPS.map((step, index) => {
            const override = stepOverrides?.[step.viewType];
            const isActive = step.viewType === activeView;
            const subtitle = resolveStepSubtitle(
              step,
              override,
              contextLabel,
              activeBoardContextLabel,
            );
            const isContextName = step.viewType === VIEW_TYPES.context && Boolean(subtitle);
            const onClick = override?.onClick ?? (() => navigateToStep(step));
            const afterStep = navAfterStep?.[step.viewType];

            return (
              <span key={step.viewType} className="contents">
                {index > 0 ? <StepChevron /> : null}
                {isActive ? (
                  <MarketingStackStepActive
                    step={step}
                    subtitle={subtitle}
                    isContextName={isContextName}
                    onShowGuide={override?.onShowGuide}
                    stepProps={override?.props}
                  />
                ) : (
                  <MarketingStackStepLink
                    step={step}
                    subtitle={subtitle}
                    isContextName={isContextName}
                    onClick={onClick}
                    stepProps={override?.props}
                  />
                )}
                {afterStep ? (
                  <>
                    <StepChevron />
                    {afterStep}
                  </>
                ) : null}
              </span>
            );
          })}
        </nav>

        {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
      </div>

      {hint ? (
        <p className="mt-1.5 px-0.5 text-xs leading-snug text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
});
